/**
 * A simple email validator (not fully to spec, but good enough)
 * @type {RegExp}
 */
export const EmailValidationRegex = /^[^@]{1,}@[^@]{1,}\.[a-zA-Z]{2,}$/;

export const PhoneNumberValidationRegex = /^[(]?([0-9]{3})[)]?[ ]?([0-9]{3})[-]?([0-9]{4})$/;
/**
 * If a match is found a string contains at least one character that is not whitespace
 * @type {RegExp}
 */
export const NotWhitespaceRegex = /\S/;

export const NoErrorsValidationMessage = "";

/**
 * Represents a validation result
 */
class ValidationResult {
  /**
   * Validation result constructor
   * @param {boolean} isValid
   * @param {string} errorMessage
   */
  constructor(isValid, errorMessage = NoErrorsValidationMessage) {
    this.isValid = isValid;
    this.errorMessage = errorMessage;
  }
  isValid = null;
  errorMessage = NoErrorsValidationMessage;

  /**
   * Return a validation state
   * @param {boolean} actuallyValidate true if the requester is ready for an actual validation result
   * @returns {null|boolean} true or false for valid / invalid respectively; null represents an unvalidated value
   */
  getState(actuallyValidate) {
    return actuallyValidate ? this.isValid : null;
  }
}

/**
 * Always returns that the value is valid
 * @returns {ValidationResult}
 */
export function AlwaysValidValidator() {
  return new ValidationResult(true);
}

/**
 * Valid if the string looks like a valid email
 * @param {string} maybeEmail
 * @param {string} [message="That doesn't look like a valid email address"]
 * @returns {ValidationResult}
 */
export function IsValidEmail(
  maybeEmail,
  message = "That doesn't look like a valid email address"
) {
  return new ValidationResult(EmailValidationRegex.test(maybeEmail), message);
}

export function IsValidPhoneNumber(
  maybePhoneNumber,
  message = "That doesn't look like a valid phone number"
) {
  return new ValidationResult(PhoneNumberValidationRegex.test(maybePhoneNumber), message);
}

/**
 * Valid if the string contains any non-whitespace characters
 * @param {string} testValue
 * @param {string} [message="A value is required"]
 * @returns {ValidationResult}
 */
export function IsNotEmptyString(testValue, message = "A value is required") {
  let isValid = true,
    type = typeof testValue;

  if ("string" === type) {
    // true if any non-whitespace characters exist
    isValid = NotWhitespaceRegex.test(testValue);
  } else if ("undefined" === type || null === testValue) {
    isValid = false;
  } else {
    // it's not a string, undefined, or null
    throw `IsNotEmptyString passed a non-string value to evaluate: ${type}`;
  }
  return new ValidationResult(isValid, message);
}

/**
 * Valid if the array is not empty
 * @param {array} testValue
 * @param {string} [message="You must select at least one"]
 * @returns {ValidationResult}
 */
export function IsNotEmptyArray(
  testValue,
  message = "You must select at least one"
) {
  return new ValidationResult(
    Array.isArray(testValue) && testValue.length > 0,
    message
  );
}

/**
 * Valid if the object has any keys defined (regardless of value)
 * @param {object} testValue
 * @param {string} [message="You must select at least one"]
 * @returns {ValidationResult}
 * @constructor
 */
export function IsNotEmptyObject(
  testValue,
  message = "You must select at least one"
) {
  return new ValidationResult(
    "object" === typeof testValue &&
      null !== testValue &&
      Object.keys(testValue).length > 0,
    message
  );
}

/**
 * Valid if the number is not less than zero
 * @param {int} testValue
 * @param {int} [message="Number greater then zero required"]
 * @returns {ValidationResult}
 */
export function IsGreaterThanZero(
  testValue,
  message = "Number greater then zero required"
) {
  let isValid = true,
    type = typeof testValue;

  if ("number" === type) {
    isValid = testValue > 0;
  } else if ("undefined" === type || null === testValue) {
    isValid = false;
  } else {
    throw `IsGreaterThanZero passed a non-integer value to evaluate: ${type}`;
  }
  return new ValidationResult(isValid, message);
}
